// Reviews: ask Claude to review a group of your stocks (held, sold, watched or all) and read past
// reviews, each expandable to the full report with a verdict per stock.
import { api } from "./api";
import { codePill, date, dateTime, esc, linkCodes, price, stockAttr, stockLink } from "./format";
import { markdown } from "./markdown";

interface ReviewStock { code: string; relation: string; action: string; conviction: string; verdict: string; key_points: string[]; risks: string[]; price: number | null }
interface Review { id: number; createdAt: string; grp: string; summary: string; stocks: ReviewStock[]; sources: { source: string; reached: boolean; note?: string }[]; emailsChecked: number; model: string | null }

const GROUPS: [string, string, string][] = [
  ["held", "What I hold", "your current holdings"],
  ["sold", "What I've sold", "stocks you've traded and no longer hold"],
  ["watched", "My watchlist", "your watchlist"],
  ["all", "Everything", "holdings, watchlist and stocks you've sold"],
];
const ACTION: Record<string, [string, string]> = {
  hold: ["Hold", ""], add: ["Add", "up"], trim: ["Trim", "warn"], sell: ["Sell", "down"], buy: ["Buy", "up"],
  watch: ["Keep watching", ""], stop_watching: ["Stop watching", "down"], revisit: ["Revisit", "up"], no_action: ["No action", ""],
};

export function reviewPrompt(group: string) {
  const g = GROUPS.find(([k]) => k === group)!;
  return `Review ${g[2]} in Gold. Call start_group_review from the Gold connector with group "${group}" and follow its guide: read everything Gold holds on each stock (including every email that mentions it and any documents I uploaded), research what has changed (news, ASX announcements, HotCopper and forums), then save the review to Gold with save_group_review, with a verdict for every stock and any key dates you find. Then give me the short version.`;
}

let group = "held";

export async function renderReviews(app: HTMLElement, query = "") {
  const openId = Number(new URLSearchParams(query).get("open")) || null;
  app.innerHTML = `<section class="reviews"><h2>Reviews</h2><div id="rv"><div class="loading" role="status"><span></span>Loading…</div></div></section>`;
  const el = document.getElementById("rv")!;
  let reviews: Review[];
  try {
    reviews = (await api<{ reviews: Review[] }>("reviews")).reviews;
  } catch (e) {
    el.innerHTML = `<div class="notice error">${esc((e as Error).message)}</div>`;
    return;
  }
  el.innerHTML = `
    <section class="card generate">
      <h3>Ask Claude to review my stocks</h3>
      <nav class="seg review-group">${GROUPS.map(([k, l]) => `<button data-g="${k}" class="${group === k ? "on" : ""}">${l}</button>`).join("")}</nav>
      <div class="gen-actions">
        <button class="primary" id="copy-review">Ask Claude to review</button>
        <a class="ghost" id="open-review" target="_blank" rel="noopener">Open in claude.ai</a>
      </div>
      <p class="muted small">Claude gets each stock's position or past trades, Gold's numbers, the latest assessment, key dates, every email that mentions it (read or unread) and any documents you've uploaded, then researches what's changed and gives a verdict on each. The button copies the request for the Claude desktop app (which can use your HotCopper login); "Open in claude.ai" works too. To review just one of your watchlists, open it on <a href="#/watchlists">Watchlists</a>.</p>
    </section>
    <h3 class="section-title">Previous reviews</h3>
    <div class="runs">${reviews.map((r, i) => card(r, openId ? r.id === openId : i === 0)).join("") || `<p class="muted">No reviews yet.</p>`}</div>`;

  const sync = () => { el.querySelector<HTMLAnchorElement>("#open-review")!.href = `https://claude.ai/new?q=${encodeURIComponent(reviewPrompt(group))}`; };
  sync();
  el.querySelectorAll<HTMLButtonElement>("[data-g]").forEach((b) => b.addEventListener("click", () => {
    group = b.dataset.g!;
    el.querySelectorAll<HTMLButtonElement>("[data-g]").forEach((x) => x.classList.toggle("on", x === b));
    const c = el.querySelector<HTMLButtonElement>("#copy-review")!;
    c.textContent = "Ask Claude to review";
    sync();
  }));
  el.querySelector("#copy-review")!.addEventListener("click", async (e) => {
    const b = e.currentTarget as HTMLButtonElement;
    try {
      await navigator.clipboard.writeText(reviewPrompt(group));
      b.textContent = "Copied: paste it into the Claude desktop app";
    } catch {
      prompt("Copy this into the Claude desktop app:", reviewPrompt(group));
    }
  });
  if (openId) el.querySelector(`[data-review="${openId}"]`)?.scrollIntoView({ block: "start" });
}

function card(r: Review, open: boolean) {
  const g = GROUPS.find(([k]) => k === r.grp)?.[1] ?? "Chosen stocks";
  const counts = new Map<string, number>();
  for (const s of r.stocks) counts.set(s.action, (counts.get(s.action) ?? 0) + 1);
  return `<details class="run card" data-review="${r.id}"${open ? " open" : ""}>
    <summary class="run-head">
      <span><b>${date(r.createdAt)}</b> <span class="muted small">· ${esc(g)} · ${r.stocks.length} stocks${r.emailsChecked ? ` · ${r.emailsChecked} emails checked` : ""}</span></span>
      <span class="picks">${r.stocks.map((s) => `<span class="pick"${stockAttr(s.code)}>${codePill(s.code)} <span class="${ACTION[s.action]?.[1] ?? ""}">${esc(ACTION[s.action]?.[0] ?? s.action)}</span></span>`).join("")}</span>
    </summary>
    <div class="run-body">
      <div class="md">${linkCodes(markdown(r.summary), r.stocks.map((s) => s.code))}</div>
      <ul class="plain review-stocks">${r.stocks.map((s) => `<li>
        <div>${stockLink(s.code)} <span class="pill">${esc(s.relation)}</span>
          <b class="${ACTION[s.action]?.[1] ?? ""}">${esc(ACTION[s.action]?.[0] ?? s.action)}</b> <span class="muted small">${esc(s.conviction)} conviction${s.price != null ? ` · ${price(s.price)} at the time` : ""}</span></div>
        <p>${linkCodes(esc(s.verdict), r.stocks.map((x) => x.code))}</p>
        ${s.key_points.length ? `<ul class="small">${s.key_points.map((x) => `<li>${esc(x)}</li>`).join("")}</ul>` : ""}
        ${s.risks.length ? `<p class="small muted">Risks: ${s.risks.map(esc).join("; ")}</p>` : ""}
        <p class="small"><a href="#/${esc(s.code)}/calendar">Calendar</a> · <a href="#/${esc(s.code)}/assess">Assess</a></p>
      </li>`).join("")}</ul>
      ${r.sources.length ? `<p class="small muted">Sources: ${r.sources.map((x) => `${esc(x.source)}${x.reached ? "" : " (not reached)"}`).join(", ")}</p>` : ""}
      <p class="small muted">Saved ${dateTime(r.createdAt)}${r.model ? ` by ${esc(r.model)}` : ""}. General information, not personal advice.</p>
    </div></details>`;
}
